<template>
  <div class="admin-table-container">
    <div class="table-info" v-if="showInfo">
      <span>
        共 {{ total }} 条记录，当前显示第 {{ startIndex }} - {{ endIndex }} 条
      </span>
    </div>

    <div class="table-wrapper" :style="{ maxHeight: maxHeight }">
      <table class="admin-table">
        <thead>
          <tr>
            <th
              v-for="column in columns"
              :key="column.key"
              :style="{ width: column.width }"
              :class="column.className"
            >
              {{ column.title }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, index) in data"
            :key="getRowKey(item, index)"
            :class="getRowClass(item, index)"
          >
            <td
              v-for="column in columns"
              :key="column.key"
              :class="column.className"
            >
              <slot
                :name="column.key"
                :item="item"
                :index="index"
                :value="getColumnValue(item, column.key)"
              >
                {{ getColumnValue(item, column.key) }}
              </slot>
            </td>
          </tr>
          <tr v-if="data.length === 0" class="empty-row">
            <td :colspan="columns.length" class="empty-cell">
              <div class="empty-state">
                <slot name="empty">
                  <p>{{ emptyText }}</p>
                </slot>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AdminTable',
  props: {
    columns: {
      type: Array,
      required: true
    },
    data: {
      type: Array,
      default: () => []
    },
    rowKey: {
      type: [String, Function],
      default: 'id'
    },
    rowClassName: {
      type: [String, Function],
      default: ''
    },
    total: {
      type: Number,
      default: 0
    },
    currentPage: {
      type: Number,
      default: 1
    },
    pageSize: {
      type: Number,
      default: 20
    },
    showInfo: {
      type: Boolean,
      default: true
    },
    maxHeight: {
      type: String,
      default: '600px'
    },
    emptyText: {
      type: String,
      default: '暂无数据'
    }
  },
  computed: {
    startIndex() {
      return this.data.length > 0 ? (this.currentPage - 1) * this.pageSize + 1 : 0
    },
    endIndex() {
      return Math.min(this.currentPage * this.pageSize, this.total)
    }
  },
  methods: {
    getRowKey(item, index) {
      if (typeof this.rowKey === 'function') {
        return this.rowKey(item, index)
      }
      return item[this.rowKey] || index
    },
    getRowClass(item, index) {
      if (typeof this.rowClassName === 'function') {
        return this.rowClassName(item, index)
      }
      return this.rowClassName
    },
    getColumnValue(item, key) {
      if (key.includes('.')) {
        return key.split('.').reduce((obj, k) => obj && obj[k], item)
      }
      return item[key]
    }
  }
}
</script>

<style scoped>
.admin-table-container {
  margin-bottom: var(--spacing-xl);
}

.table-info {
  margin-bottom: var(--spacing-sm);
  font-size: var(--font-size-md);
  color: var(--text-secondary);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.table-wrapper {
  overflow: auto;
  border: 1px solid var(--border-light);
  border-radius: var(--border-radius-sm);
  margin-bottom: var(--spacing-lg);
}

.table-wrapper::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.table-wrapper::-webkit-scrollbar-track {
  background: var(--bg-primary);
  border-radius: var(--border-radius-sm);
}

.table-wrapper::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: var(--border-radius-sm);
}

.table-wrapper::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.admin-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 0;
  background: var(--bg-white);
}

.admin-table th,
.admin-table td {
  padding: var(--spacing-md) var(--spacing-lg);
  text-align: left;
  border-bottom: 1px solid var(--border-light);
  white-space: nowrap;
}

.admin-table th {
  background-color: var(--bg-primary);
  font-weight: 600;
  color: var(--text-primary);
  position: sticky;
  top: 0;
  z-index: 10;
  font-size: var(--font-size-md);
}

.admin-table tbody tr:hover {
  background-color: rgba(0, 123, 255, 0.05);
}

.admin-table tbody tr.highlighted {
  background-color: #e3f2fd;
}

.admin-table tbody tr.highlighted td {
  border-left: 3px solid var(--primary-color);
}

.empty-row {
  background: none !important;
}

.empty-cell {
  border-bottom: none !important;
}

.empty-state {
  text-align: center;
  color: var(--text-muted);
  font-style: italic;
  padding: var(--spacing-xxl) var(--spacing-xl);
}

.empty-state p {
  margin: 0;
  font-size: var(--font-size-lg);
}

@media (max-width: 768px) {
  .admin-table th,
  .admin-table td {
    padding: var(--spacing-sm) var(--spacing-md);
    font-size: var(--font-size-sm);
  }
}
</style>